import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchNewsArticleById } from '../services/newsArticleService';
import type { NewsArticle } from '../types/newsArticle';
import NewsCommentSection from '../components/NewsCommentSection';

const NewsArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchNewsArticleById(id).then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data) {
        setNotFound(true);
      } else {
        setArticle(data);
      }
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [id]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  if (loading) {
    return (
      <div className="bg-[#080808] text-white min-h-screen flex items-center justify-center">
        <p style={{ color: 'rgba(255,255,255,0.4)' }}>Laster artikkel…</p>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="bg-[#080808] text-white min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-5xl font-bold text-white mb-4">404</h1>
          <p className="text-xl text-gray-400 mb-8">Fant ikke artikkelen</p>
          <button type="button" onClick={() => navigate('/nyheter')} className="btn-primary py-3 px-8 font-bold">
            Tilbake til nyheter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#080808] text-white min-h-screen overflow-hidden">

      {/* Hero image — full width, bleeds to edges */}
      {article.image_url && (
        <div className="w-full" style={{ maxHeight: '520px', overflow: 'hidden' }}>
          <img
            src={article.image_url}
            alt={article.title}
            className="w-full object-cover"
            style={{ maxHeight: '520px', objectPosition: 'center' }}
          />
        </div>
      )}

      <div className="section-padding">
        <div className="container-responsive max-w-3xl">

          {/* Back link */}
          <button
            type="button"
            onClick={() => navigate('/nyheter')}
            className="inline-flex items-center gap-2 text-sm mb-8 transition-colors"
            style={{ color: 'rgba(255,255,255,0.35)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
            </svg>
            Nyheter
          </button>

          {/* Category tag */}
          <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Smuglergods · Nyheter
          </p>

          {/* Headline */}
          <h1 className="text-3xl sm:text-5xl font-bold text-white tracking-tight leading-tight mb-6">
            {article.title}
          </h1>

          {/* Byline */}
          <div
            className="flex items-center gap-3 pb-6 mb-8"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
          >
            {/* Author avatar placeholder */}
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}
            >
              {article.author ? article.author.charAt(0).toUpperCase() : 'S'}
            </div>
            <div>
              <p className="text-sm font-semibold text-white/70">
                {article.author ?? 'Smuglergods'}
              </p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                Publisert {formatDate(article.created_at)}
                {article.updated_at && article.updated_at !== article.created_at && (
                  <span className="ml-2">(oppdatert {formatDate(article.updated_at)})</span>
                )}
              </p>
            </div>
          </div>

          {/* Body */}
          <div
            className="leading-relaxed whitespace-pre-wrap text-base sm:text-lg"
            style={{ color: 'rgba(255,255,255,0.82)', lineHeight: '1.85' }}
          >
            {article.content}
          </div>

          {/* Comments */}
          <NewsCommentSection articleId={article.id} />

          {/* Footer */}
          <div className="mt-12 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <button
              type="button"
              onClick={() => navigate('/nyheter')}
              className="btn-secondary py-2.5 px-6 text-sm font-bold"
            >
              ← Tilbake til nyheter
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default NewsArticlePage;
